import { getSubject } from './index';
import { getUiSoundPlayer } from '../ui-sounds';
import {
  isRealUnitChapter,
  chapterGrade,
  DEFAULT_GRADE_SEMESTER,
} from '../review/exam-center-route';
import { mountBackButton } from '../nav/back-button';
import { ensureAcademicTheme } from '../ui/theme';
import type { Subject, GradeSemester } from '../../shared/types';
import type { ChapterEntry } from './index';

export interface ChapterListActions {
  onSelectChapter: (subjectId: Subject, chapterNum: number) => void;
  onBack: () => void;
}

export const THREE_UP_PREFIX = '【三上】';

export function isThreeUpChapter(title: string): boolean {
  return title.startsWith(THREE_UP_PREFIX);
}

/**
 * Current-semester (三下) chapters have no 【x】 prefix — distinguishes them from
 * 三上 / 四上 review chapters that share the same subject curriculum list.
 */
export function isCurrentSemesterChapter(title: string): boolean {
  return !title.startsWith('【');
}

/**
 * 「學 → 看課本」當前學年過濾(boss 2026-06-10「學/練/考只列當前學年單元;複習才把
 * 舊的拿出來」)。沿用 `listExamUnits` 同一套學年判定 —— `isRealUnitChapter`(排除段考
 * 真題庫 number 50 / 標題含 📚 / 全 KP `*.meow911`)+ `chapterGrade`(取章內 KP 的
 * `grade`,未填預設 3.2)。**不另發明判定**。
 *
 * 回傳「該學年的真課本單元」(依原 curriculum 序)。空集合 = 該科該學年尚未建任何單元
 * (例:四上只有數學有,國/英/自/社無)→ 由呼叫端走 fallback(顯示建置中 + 退列全部),
 * 不變空白死頁。
 */
export function chaptersForGrade(
  chapters: ReadonlyArray<ChapterEntry>,
  gradeSemester: GradeSemester,
): ChapterEntry[] {
  return chapters.filter((c) => isRealUnitChapter(c) && chapterGrade(c) === gradeSemester);
}

/** 該科所有「真課本單元」(排除段考真題庫等綜合池),不限學年 —— 空集合 fallback 用。 */
export function realUnitChapters(chapters: ReadonlyArray<ChapterEntry>): ChapterEntry[] {
  return chapters.filter((c) => isRealUnitChapter(c));
}

/**
 * 「學 → 看課本」章節清單。
 *
 * @param gradeSemester 要列的學年(預設三下 3.2)。只列當前學年的課本單元;舊學年單元
 *   不在此(在「複習」入口)。呼叫端傳 `gradeSemesterOf(loadProgress())`,改設定頁學年
 *   → 此清單即時跟著變。
 */
export function renderChapterList(
  container: HTMLElement,
  subjectId: Subject,
  actions: ChapterListActions,
  gradeSemester: GradeSemester = DEFAULT_GRADE_SEMESTER,
): void {
  const s = getSubject(subjectId);
  if (!s) { actions.onBack(); return; }

  // 當前學年的真課本單元(沿用 listExamUnits 同套判定)。
  let chapters = chaptersForGrade(s.chapters, gradeSemester);
  // 空集合 fallback:該科該學年尚未建單元 → 退列全部真單元 + 標註建置中,不變空白死頁。
  const usedFallback = chapters.length === 0;
  if (usedFallback) chapters = realUnitChapters(s.chapters);

  ensureAcademicTheme();
  const root = document.createElement('div');
  root.className = 'chapter-list';
  // boss 2026-06-14「回上一頁方式要統一」:只用左上統一 ‹ 返回(下方 mountBackButton),
  // 不再放底部「回學院」鈕,避免一頁兩個返回鈕。
  root.innerHTML = `
    <h1>${s.emoji} ${s.label} (${s.textbook})</h1>
    <div class="chapter-rows"></div>
  `;
  container.appendChild(root);

  // 統一 ‹ 返回:左上一致返回鈕 + 註冊集中式返回堆疊(左緣滑動共用)。
  const back = (): void => { offBack(); root.remove(); actions.onBack(); };
  const offBack = mountBackButton(root, { onBack: back });

  const rows = root.querySelector('.chapter-rows')!;

  // 標頭:本學期重點(或 fallback 提示)。過濾後同學年單元,只需一個 current 標頭。
  const header = document.createElement('div');
  header.className = 'chapter-section-header chapter-section-header--current';
  if (usedFallback) {
    header.classList.add('chapter-section-header--fallback');
    header.innerHTML = `
      <div class="section-title">📖 課本單元</div>
      <div class="section-subtitle">(此學年課本內容建置中,先顯示全部單元)</div>
    `;
  } else {
    header.innerHTML = `
      <div class="section-title">📖 課本單元(本學期重點)</div>
      <div class="section-subtitle">(今天學的章節,主力修煉值來源)</div>
    `;
  }
  rows.appendChild(header);

  for (const c of chapters) {
    const allReady = c.kps.every(k => k.status === 'ready');
    const someReady = c.kps.some(k => k.status === 'ready');
    const statusLabel = allReady ? '✅ 開放中' : someReady ? '🟡 部分內容' : '⏳ 製作中';

    const row = document.createElement('div');
    row.className = 'chapter-row' + (someReady ? '' : ' disabled');
    row.innerHTML = `
      <div class="ch-num">第 ${c.number} 章</div>
      <div class="ch-title">${c.title}</div>
      <div class="ch-status">${statusLabel}</div>
      <div class="ch-kps">${c.kps.length} 個學習點</div>
    `;
    if (someReady) {
      row.addEventListener('click', () => {
        getUiSoundPlayer().play('ui-click', 0.3);
        offBack();
        root.remove();
        actions.onSelectChapter(subjectId, c.number);
      });
    }
    rows.appendChild(row);
  }
}
